<template>
  <LoadingDiv v-if="!isReady" />

  <template v-else>
    <form class="settings-workspace" @submit.prevent="handleSubmit">
      <div class="settings-grid">
        <aside class="settings-summary">
          <label class="active teal-text">Client Settings</label>
          <h5>{{ cat == "tpa" ? "TPA" : "Policy" }} Controls</h5>
          <p>
            Core policy boundaries used for enrollee onboarding and billing
            checks.
          </p>

          <div class="summary-list">
            <div class="summary-item">
              <span>Policy Window</span>
              <strong>
                {{
                  new Date(`${year}-${month}-01`).toLocaleString(undefined, {
                    year: "numeric",
                    month: "short",
                  })
                }}
                -
                {{
                  new Date(
                    `${Number(year) + 1}-${Number(month) - 1}-01`
                  ).toLocaleString(undefined, {
                    year: "numeric",
                    month: "short",
                  })
                }}
              </strong>
            </div>

            <div class="summary-item">
              <span>Payment Cycle</span>
              <strong>Every {{ premiumCycle }} Month(s)</strong>
            </div>
          </div>
        </aside>

        <section class="settings-main">
          <div v-if="error" class="settings-error">
            <i class="material-icons">error</i>
            <span>{{ error }}</span>
          </div>

          <div class="setting-card locked-card">
            <div class="setting-copy">
              <label class="active teal-text">
                {{ cat == "tpa" ? "TPA" : "Policy" }} Year
              </label>
              <h6>
                {{
                  new Date(`${year}-${month}-01`).toLocaleString(undefined, {
                    year: "numeric",
                    month: "short",
                  })
                }}
                -
                {{
                  new Date(
                    `${Number(year) + 1}-${Number(month) - 1}-01`
                  ).toLocaleString(undefined, {
                    year: "numeric",
                    month: "short",
                  })
                }}
              </h6>
              <small>12 month policy period</small>
            </div>
            <span class="locked-chip">
              <i class="material-icons">lock</i>
              Locked
            </span>
          </div>

          <div class="setting-card locked-card">
            <div class="setting-copy">
              <label class="active teal-text">Payment Cycle</label>
              <h6>Every {{ premiumCycle }} Month(s)</h6>
              <small>Used for invoice group generation</small>
            </div>
            <span class="locked-chip">
              <i class="material-icons">lock</i>
              Locked
            </span>
          </div>

          <div class="setting-card" :class="{ editing: editId == 'familySize' }">
            <div class="setting-copy">
              <label class="active teal-text" for="familySize">Family Size</label>
              <h6 v-if="editId != 'familySize'">{{ familySize }} Members</h6>
              <div class="input-field" v-else>
                <input
                  id="familySize"
                  type="number"
                  v-model="familySize"
                  class="validate"
                  required
                />
              </div>
            </div>
            <div class="setting-actions">
              <a
                v-if="editId != 'familySize'"
                href="#"
                class="icon-action"
                @click.prevent="handleEdit('familySize')"
              >
                <i class="material-icons" title="edit">edit</i>
              </a>
              <template v-else>
                <a href="#" class="text-action" @click.prevent="handleBack">
                  Cancel
                </a>
                <button class="waves-effect waves-light btn" :class="{ disabled: isSubmitting }">
                  Update
                  <i class="material-icons right" title="update">check_circle</i>
                </button>
              </template>
            </div>
          </div>

          <div class="setting-card" :class="{ editing: editId == 'principalAge' }">
            <div class="setting-copy">
              <label class="active teal-text" for="principalAge">Principal Age</label>
              <h6 v-if="editId != 'principalAge'">{{ principalAge }} Years</h6>
              <div class="input-field" v-else>
                <input
                  id="principalAge"
                  type="number"
                  v-model="principalAge"
                  class="validate"
                  required
                />
              </div>
            </div>
            <div class="setting-actions">
              <a
                v-if="editId != 'principalAge'"
                href="#"
                class="icon-action"
                @click.prevent="handleEdit('principalAge')"
              >
                <i class="material-icons" title="edit">edit</i>
              </a>
              <template v-else>
                <a href="#" class="text-action" @click.prevent="handleBack">
                  Cancel
                </a>
                <button class="waves-effect waves-light btn" :class="{ disabled: isSubmitting }">
                  Update
                  <i class="material-icons right" title="update">check_circle</i>
                </button>
              </template>
            </div>
          </div>

          <div class="setting-card" :class="{ editing: editId == 'dependantAge' }">
            <div class="setting-copy">
              <label class="active teal-text" for="dependantAge">Dependant Age</label>
              <h6 v-if="editId != 'dependantAge'">{{ dependantAge }} Years</h6>
              <div class="input-field" v-else>
                <input
                  id="dependantAge"
                  type="number"
                  v-model="dependantAge"
                  class="validate"
                  required
                />
              </div>
            </div>
            <div class="setting-actions">
              <a
                v-if="editId != 'dependantAge'"
                href="#"
                class="icon-action"
                @click.prevent="handleEdit('dependantAge')"
              >
                <i class="material-icons" title="edit">edit</i>
              </a>
              <template v-else>
                <a href="#" class="text-action" @click.prevent="handleBack">
                  Cancel
                </a>
                <button class="waves-effect waves-light btn" :class="{ disabled: isSubmitting }">
                  Update
                  <i class="material-icons right" title="update">check_circle</i>
                </button>
              </template>
            </div>
          </div>
        </section>
      </div>
    </form>
  </template>
</template>
    
<script>
import { onMounted, onUpdated, ref } from "vue";
import getItemsOfItems from "@/composables/getItemsOfItems";
import putItem from "@/composables/putItem";
import LoadingDiv from "@/components/LoadingDiv.vue";
import M from "materialize-css";

export default {
  name: "SettingsClientComponent",
  components: { LoadingDiv },
  props: ["cat", "clientId"],
  emits: ["next"],
  setup(props) {
    // Workmen
    const response = ref(null);
    const error = ref(null);
    const isSubmitting = ref(false);
    const isReady = ref(false);
    const editId = ref(null);
    const year = ref(null);
    const month = ref(null);

    // Rules table
    const policyDateMonth = ref(null);
    const policyDateYear = ref(null);
    const premiumCycle = ref(null);
    const familySize = ref(null);
    const principalAge = ref(null);
    const dependantAge = ref(null);

    // get settings
    const getItemsFromApi = () => {
      // set current Url
      let currUrl = "policy/client";

      // get items
      (async () => {
        const { res, err } = await getItemsOfItems(currUrl, `${props.clientId}/current`);

        if (res.value) {
          // get
          response.value = res.value.data;
          // load form variables
          loadVariables();
        } else {
          error.value = err.value;
        }

        // ready
        isReady.value = true;
        // clear edit (if required)
        editId.value = null;
      })();
    };

    // start here
    onMounted(() => {
      // get settings
      getItemsFromApi();
    });

    // select
    onUpdated(() => {
      // activate select field
      const elemsSelect = document.querySelectorAll("select");
      M.FormSelect.init(elemsSelect, {});
    });

    // handle form variables
    const loadVariables = () => {
      // clear all
      editId.value = null;
      policyDateMonth.value = null;
      policyDateYear.value = null;
      premiumCycle.value = null;
      familySize.value = null;
      principalAge.value = null;
      dependantAge.value = null;

      const startDate = response.value?.start_date || response.value?.start;
      if (startDate) {
        const start = new Date(startDate);
        const monthValue = String(start.getMonth() + 1).padStart(2, "0");
        const yearValue = String(start.getFullYear());

        policyDateMonth.value = monthValue;
        policyDateYear.value = yearValue;
        year.value = yearValue;
        month.value = monthValue;
      }

      premiumCycle.value = response.value?.cycle || null;
      familySize.value = response.value?.family_size || null;
      principalAge.value = response.value?.principal_age || null;
      dependantAge.value = response.value?.dependant_age || null;
    };

    // handle Edit
    const handleEdit = (val) => {
      editId.value = val;
    };

    // handle Back
    const handleBack = () => {
      // load form variables
      loadVariables();
    };

    // submit
    const handleSubmit = async () => {
      if (!editId.value) {
        return;
      }

      // start
      isSubmitting.value = true;
      error.value = false;

      const policyFields = {
        familySize: { family_size: familySize.value },
        principalAge: { principal_age: principalAge.value },
        dependantAge: { dependant_age: dependantAge.value },
      };

      const newVal = policyFields[editId.value];

      if (!newVal) {
        isSubmitting.value = false;
        return;
      }

      // post item
      const { res, err } = await putItem("policy", response.value.id, newVal);

      if (res.value) {
        // next
        // get items
        isReady.value = false;
        // call settings
        getItemsFromApi();
      } else {
        error.value = err.value;
      }

      // end here
      isSubmitting.value = false;
    };

    return {
      error,
      isReady,
      editId,
      handleEdit,
      handleBack,
      isSubmitting,
      handleSubmit,
      year,
      month,
      policyDateMonth,
      policyDateYear,
      premiumCycle,
      familySize,
      principalAge,
      dependantAge,
    };
  },
};
</script>
    
<style scoped>
.settings-workspace {
  margin-top: 8px;
}

.settings-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(240px, 0.85fr) minmax(320px, 1.65fr);
}

.settings-summary,
.setting-card {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  box-shadow: none;
}

.settings-summary {
  align-self: start;
  padding: 18px;
}

.settings-summary h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 8px;
}

.settings-summary p {
  color: #607d8b;
  line-height: 1.55;
  margin: 0 0 16px;
}

.summary-list {
  border-top: 1px solid #eeeeee;
  display: grid;
  gap: 12px;
  padding-top: 14px;
}

.summary-item {
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  padding: 12px;
}

.summary-item span,
.setting-copy small {
  color: #78909c;
  display: block;
  font-size: 0.82rem;
}

.summary-item strong {
  color: #263238;
  display: block;
  font-size: 0.98rem;
  margin-top: 4px;
}

.settings-main {
  display: grid;
  gap: 14px;
}

.setting-card {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  min-height: 86px;
  padding: 16px;
}

.setting-card.editing {
  background: #fafafa;
}

.setting-copy {
  min-width: 0;
}

.setting-copy h6 {
  color: #263238;
  font-size: 1.08rem;
  font-weight: 700;
  margin: 6px 0 3px;
}

.setting-copy .input-field {
  margin: 8px 0 0;
  max-width: 240px;
}

.setting-actions {
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 12px;
}

.icon-action,
.locked-chip {
  align-items: center;
  border-radius: 18px;
  display: inline-flex;
  justify-content: center;
}

.icon-action {
  background: #e3f2fd;
  color: #1565c0;
  height: 38px;
  width: 38px;
}

.icon-action i {
  font-size: 20px;
}

.locked-chip {
  background: #eceff1;
  color: #607d8b;
  cursor: default;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 6px;
  padding: 7px 12px;
}

.locked-chip i {
  font-size: 17px;
}

.text-action {
  color: #607d8b;
  font-weight: 700;
}

.settings-error {
  align-items: center;
  background: #ffebee;
  border-left: 4px solid #c62828;
  border-radius: 4px;
  color: #b71c1c;
  display: flex;
  gap: 10px;
  padding: 10px 12px;
}

@media (max-width: 850px) {
  .settings-grid {
    grid-template-columns: 1fr;
  }

  .setting-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .setting-actions {
    justify-content: flex-end;
    width: 100%;
  }
}
</style>
